import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, provideRouter, RouteReuseStrategy } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { customThemePreset } from '../custom-theme-preset';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { errorInterceptor } from './common/interceptors/error.interceptor';
import { loaderInterceptor } from './common/interceptors/loader.interceptor';
import { InsightsService } from './common/services/insights.service';

export function loggerCallback(logLevel: LogLevel, message: string) {
  // console.log(logLevel, message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: '/redirect',
      postLogoutRedirectUri: '/logged-out',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      popupBridgeTimeout: 90000, // 90 seconds for user to log in and complete MFA
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    environment.apiConfig.graphUri,
    environment.apiConfig.graphScopes
  );
  protectedResourceMap.set(
    environment.apiConfig.apiEndpointBase,
    [environment.apiConfig.apiScope]
  );

  return {
    interactionType: InteractionType.Popup,
    // interactionType: InteractionType.Redirect,
    protectedResourceMap,
    strictMatching: false
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Popup,
    // interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.graphScopes, environment.apiConfig.apiScope],
      // domainHint: 'steward.org', // suggests that single sign-on should use this domain
      prompt: 'select_account' // forces account selection
    },
    loginFailedRoute: '/not-authenticated',
  };
}

export class CustomReuseStrategy extends BaseRouteReuseStrategy {
  // When the page is not-authenticated, and the user bails on authentication
  // The component instance must be rebuilt not reused
  override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return ( future.routeConfig?.path === '/not-authorized' && curr.routeConfig?.path === future.routeConfig?.path);
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    importProvidersFrom(
      BrowserModule
    ),
    provideHttpClient(
      withInterceptors([loaderInterceptor, errorInterceptor]),
      withInterceptorsFromDi(),
      withFetch()
    ),
    {
      provide: RouteReuseStrategy,
      useClass: CustomReuseStrategy
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    providePrimeNG({
      theme: {
        preset: customThemePreset,
        options: {
          darkModeSelector: '.qh-dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng'
          }
        }
      },
    }),
    InsightsService,
    {
      provide: ErrorHandler,
      useClass: ApplicationinsightsAngularpluginErrorService,
    },
    MessageService
  ],
};